/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import axios from 'axios';
import { EitherAsync, identity, Right } from 'purify-ts';
import * as codec from 'purify-ts/Codec';
import * as compression from '@codegrade/compression';
import { errors, object } from '@codegrade/utils';
import { parseBool } from './boolean';
/**
 * Wraps `axios.get()` for retrieving arraybuffer without parsing to JSON automatically.
 *
 * @param route The route from which to retrieve the array buffer.
 * @returns Either the Axios response, or an error occurring during the get operation .
 */
function getArrayBufferWithAxios(route) {
    /*
     * Axios automatically uses JSON.parse on everything they get,
     * even if you specify another response type.
     * So if you are downloading a JSON file, you get an object instead of an
     * ArrayBuffer. Overriding the transformResponse function avoids that.
     */
    return EitherAsync(() => axios.get(route, { responseType: 'arraybuffer', transformResponse: identity })).mapLeft(errors.coerceToError);
}
// https://stackoverflow.com/questions/13405129/javascript-create-and-save-file
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function downloadFile(data, filename, contentType) {
    const file = new Blob([data], { type: contentType });
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 0);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function downloadMirrorFile(file) {
    window.open(file.url);
}
const SignedUrlCodec = codec.Codec.interface({ url: codec.string });
/**
 * Retrieves a File from S3 storage as an ArrayBuffer using Axios starting form either the API
 * route to retrieve the s3 signed link, or the signed link itself.
 *
 * @param route The API route to retrieve the S3 signed link or the the link itself.
 * @returns Either an error occurring during the operation or the ArrayBuffer with the file data.
 */
export function downloadRawFile(route) {
    // Cypress overwrites the `fetch` function but their implementation is broken
    // for binary data. It does not define an `arrayBuffer` method on the
    // `Response` it returns. The response does, however, have a `blob` method,
    // but the data it contains is incorrect, leading to images and pdf files not
    // being rendered. Performing the requests with axios works fine, though.
    return getArrayBufferWithAxios(route).chain(response => {
        const headers = response.headers;
        if (headers != null && parseBool(headers['x-cg-is-url'], false)) {
            /* As the data is an array buffer, we need to decode the data before parsing. */
            const parsedResponseData = JSON.parse(new TextDecoder().decode(response.data));
            return EitherAsync.liftEither(SignedUrlCodec.decode(parsedResponseData))
                .mapLeft(err => new Error(err))
                .chain(signedUrl => getArrayBufferWithAxios(signedUrl.url).map(object.attr('data')));
        }
        return EitherAsync.liftEither(Right(response.data));
    });
}
/**
 * Downloads a file from an S3 signed link.
 *
 * @param signedS3link The signed S3 link to download the file from.
 * @returns The file as an `ArrayBuffer`, or an error if the get fails.
 */
export function downloadFileFromS3(signedS3link) {
    return getArrayBufferWithAxios(signedS3link).map(object.attr('data'));
}
/**
 * Downloads a compressed file from an S3 signed link and decompresses it.
 *
 * @param signedS3link The signed S3 link to download the file from.
 * @returns The file as a `Uint8Array`, or an error if the get fails.
 */
export function downloadCompressedFileFromS3(signedS3link) {
    return downloadFileFromS3(signedS3link)
        .chain(async (data) => compression.CompressionResult.fromCompressedBinary(new Uint8Array(data)))
        .chain(compressed => compressed.decompress());
}
